<script type="ts">
import { ScoreMethod } from "../browser";
import ImageRender from "../components/ImageRender.svelte";

let baColorize = false;
let baCropping = false;
let baPlating = false;
let baEmir = false;
</script>

<section>
    <h2>Approach</h2>
    <p>The basic colorization pipeline looks like this:</p>
    <ol>
        <li>(optional) Perform any <b>preprocessing</b> for each plate image</li>
        <li>Split the plate image into three equal parts for each channel</li>
        <li>Align each channel with each other (determine the offset)</li>
        <li>(optional) Perform any processing on each channel (contrast, white balance, etc.)</li>
        <li>Layer each channel on each other to form a color image!</li>
    </ol>
    <p>Each process will be discussed below in detail.</p>

    <h3>Colorizing</h3>
    <p>
        As mentioned before, we want to split the image into three separate images (channels) of equal height that will eventually be aligned together to form a complete color image. 
        Of course, our collection of color plates is not perfect and we don't expect everything to be completely aligned. We try to mitigate this by offseting some channels by small amounts so that they can align more closely and determine the best "fitted" offsets.
        In the case of this project, for a simple render we iterate over the range of (-15, 15) for x and y.
    </p>

    <p>Below is an example of a simple plate being colorized by our simple colorization algorithm:</p>
    <button on:click="{() => baColorize = !baColorize}">
        {#if baColorize}Undo colorize{:else}Apply colorize{/if}
    </button>
    <ImageRender options={{
        multiscale: false,
        cropping: false,
        colorize: baColorize,
        score: ScoreMethod.SCORE_SSD
    }} image="cathedral"/>

    
    <h4>Comparison of SSD and NCC</h4>
    <p>
        In order to align each image computationally, we score each possible alignment of two channels (iterated over a range of close x and y values) by comparing their "likeness" with a metric and then take the best scoring alignment as our channel offset.
        We want to make our judgements consistent, so we use a base channel to compare all the other channels with, performing two passovers with the metric in total. 
    </p>
    <p>
        There are two scoring methods we can use: <b>SSD (sum of squared differences)</b> and <b>NCC (normalized cross-correlation)</b>. While both of these metrics have been used to calculate the offsets of the channels, I found that the results between the two do not change very significantly and in fact calculate the same offsets.
        However, it's important to note that SSD should be minimized and NCC should be maximized in order to provide the most matching results; otherwise, the score will optimize towards the other direction and prioritize offsets with least fit.
    </p>

    <h4>Base Color Channel</h4>
    <p>
    By default, the base starter code uses the blue color channel as the base channel to compare all other channels with during alignment. While this initially made sense since the blue channel was the first slice of the plate, I eventually ran into a problem with one of my earlier renders where the program did not appear to align correctly. Recalling from lecture that we sense more green, I changed the base to green and got a much better result! Performing this change and performing some cropping also allowed me to get a good looking result for Emir.
    </p>

    <div class="flex-gallery">
        <div class="flex-gallery-item">
            <img src="extra/no_aligned_monastery.jpg" alt="monastery with no post-alignment" width="240"/>
            <div class="flex-gallery-caption">monastery with no post-alignment</div>
        </div>
        <div class="flex-gallery-item">
            <img src="extra/blue_aligned_monastery.jpg" alt="Aligned monastery with blue as base" width="240"/>
            <div class="flex-gallery-caption">Aligned monastery with blue as base</div>
        </div>
        <div class="flex-gallery-item">
            <img src="extra/yes_aligned_monastery.jpg" alt="Aligned monastery with green as base" width="240"/>
            <div class="flex-gallery-caption">Aligned monastery with green as base</div>
        </div>
    </div>

    <h3>Multiscaling</h3>
    <p>
        It's infeasible to map through larger and larger offsets as image inputs become larger in dimension. This became the case when we work with the original plates, which can reach thousands of pixels for each dimension.
        To mitigate this, I implemented a multiscaling algorithm that could recursively scale down the image down to a reasonable space, discover the offset for the smaller image, then progressively scale up and adjust the offset by small amounts.
    </p>
    <p>
        For my implementation, I found that scaling the image down by 2 over 9 total times has generated successful results, where the base case (smallest image) iterates over an x/y delta of (-15, 15) and progressive layers after this by (-4, 4).
    </p>
    <p>The multiscaling ended up being a pretty fast operation, running on an average of 20 to 30 seconds per original plate!</p>

    <h3>Cropping (extra credit)</h3>
    <p>
        One of the biggest optimizations I made to this project was implementing frame cropping to the original plate so that the crops could be aligned cleanly later.
        When I was initially implementing the colorization, I ran into alignment issues with emir which made it look pretty unsatisfactory and unaligned. To cut off the large borders,
        I implemented an algorithm that would seek a solid grayscale border (more specifically by taking in n pixels from each side and comparing the aggregate values to a black/white threshold to determine when to cut off),
        which was possible due to the format of each plate having a known white border and an inner black border.
    </p>
    <p>After alignment and cropping, the new pictures look really really good (including emir)! For some cases (such as villager), there is a slightly aggressive crop and some content is lost, but some cases (like interior) that I expected to crop poorly (due to the solid white/black background) actually cropped really well.</p>

    <button on:click="{() => baCropping = !baCropping}">
        {#if baCropping}Remove cropping{:else}Add cropping{/if}
    </button>
    <button on:click="{() => baPlating = !baPlating}">
        {#if baPlating}Show plating{:else}Show colorized{/if}
    </button>
    <button on:click="{() => baEmir = !baEmir}">
        {#if baEmir}Show boulder{:else}Show emir{/if}
    </button>
    <ImageRender options={{
        multiscale: true,
        cropping: baCropping,
        colorize: !baPlating,
        score: ScoreMethod.SCORE_SSD
    }} image="{baEmir ? 'emir' : 'boulder'}"/>
</section>